import { useState } from "react";
import type { Offer, ProductMatch, RecommendationContract } from "../types";
import styles from "./RecommendationCard.module.css";

// Offer small print carries real links ("For Detailed T&C, Please visit:
// https://…"). Rendering them as plain text would strand the customer, so the
// URLs become real anchors and everything around them stays literal text.
const URL_RE = /(https?:\/\/[^\s,]+)/g;

function LinkedText({ text }: { text: string }) {
  const parts = text.split(URL_RE);
  return (
    <>
      {parts.map((part, i) =>
        URL_RE.test(part) && /^https?:\/\//.test(part) ? (
          <a
            key={i}
            className={styles.offerLink}
            href={part}
            target="_blank"
            rel="noreferrer noopener"
          >
            {part.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </a>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

/** The always-visible offers strip at the bottom of a product card, and the
 *  panel of full offer detail it opens when clicked.
 *
 *  Deliberately NOT inside the card's hover-revealed spec sheet: an offer is a
 *  reason to choose this product, so it has to be visible before the customer
 *  has any reason to hover. The spec sheet keeps behaving exactly as it did. */
function OffersBar({
  offers,
  open,
  onToggle,
}: {
  offers: Offer[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={styles.offerCell}>
      <button
        type="button"
        className={styles.offerToggle}
        aria-expanded={open}
        onClick={(e) => {
          // The card's link is an overlay behind this button, so the click
          // already lands here — these two keep it that way even if the
          // stacking changes.
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
      >
        {/* Count comes from however many the API sent for THIS product —
            never a fixed number. Singular when there is exactly one, and the
            whole strip is not rendered at all when there are none. */}
        <span className={styles.offerTag}>
          {offers.length} {offers.length === 1 ? "offer" : "offers"} available
        </span>
        <span className={styles.offerToggleHint}>
          {open ? "Hide" : "View"}
        </span>
        <span className={open ? styles.offerCaretOpen : styles.offerCaret}>▾</span>
      </button>
      {open && (
        <div className={styles.offerPanel}>
          {offers.map((offer) => (
            <div key={offer.heading} className={styles.offerItem}>
              <div className={styles.offerHeading}>{offer.heading}</div>
              {offer.content.length > 0 && (
                <ul className={styles.offerContent}>
                  {offer.content.map((line, i) => (
                    <li key={i}>
                      <LinkedText text={line} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface Props {
  /** Every game researched so far in the conversation, not only this turn.
   * A build settled over several turns of clarification is searched one
   * game at a time, so this turn's queries name only the last one. */
  games?: string[];

  contract: RecommendationContract;
  onAction: (text: string) => void;
}

const CATEGORY_LABEL: Record<string, string> = {
  gaming: "Gaming",
  ai_ml: "AI / ML",
  content_creation: "Creative",
  everyday: "Everyday",
  programming: "Programming",
};

// The backend only assigns "official" when a search actually returned figures
// (see assemble_contract); anything else is a category baseline and must not be
// dressed up as researched fact.
const SOURCE_LABEL: Record<string, string> = {
  official: "Official specs (web)",
  community_reported: "Community specs (web)",
  estimated: "Typical build for this use",
};

/** Decide whether this contract is worth rendering as a rich card. */
export function shouldShowCard(c: RecommendationContract | null | undefined): boolean {
  if (!c) return false;
  const p = c.recommended_spec_profile;
  const f = c.research.key_findings;
  return Boolean(
    (c.product_match && c.product_match.length > 0) ||
      p.gpu_tier ||
      p.cpu_tier ||
      p.ram_gb ||
      p.storage ||
      p.display ||
      f.recommended_vram_gb ||
      f.recommended_ram_gb ||
      f.min_vram_gb,
  );
}

// The card's own labels, in the language the reply was written in.
//
// Reported live: a Hindi conversation was answered in Hindi and then handed a
// card reading "SPEC BRIEF", "MATCHING DELL LAPTOPS" and "Fits the target" —
// the advice in one language, the furniture around it in another. English is
// the fallback for every language not listed, including Hinglish, which is
// written in Latin letters and whose readers expect the English terms.
type CardLabels = {
  specBrief: string;
  sizedFor: string;
  sizedForN: (n: number) => string;
  config: string;
  basis: string;
  baseline: string;
  matchingLaptops: string;
  matchingDesktops: string;
  matchingBoth: string;
  pick: string;
  fits: string;
  close: string;
  overBudget: (amount: string) => string;
  whichToBuy: string;
  yourBudget: string;
  showFor: (amount: string) => string;
  compareHead: string;
  storage: string;
  display: string;
  price: string;
  stock: string;
};

const EN_LABELS: CardLabels = {
  specBrief: "SPEC BRIEF",
  sizedFor: "SIZED FOR",
  sizedForN: (n) => `SIZED FOR ${n} GAMES`,
  config: "RECOMMENDED CONFIGURATION",
  basis: "BASIS",
  baseline: "Recommended baseline",
  matchingLaptops: "MATCHING DELL LAPTOPS",
  matchingDesktops: "MATCHING DELL DESKTOPS",
  matchingBoth: "MATCHING DELL LAPTOPS & DESKTOPS",
  pick: "OUR PICK",
  fits: "FITS",
  close: "CLOSE",
  overBudget: (a) => `${a} over budget`,
  whichToBuy: "WHICH LAPTOP SHOULD I BUY?",
  yourBudget: "YOUR BUDGET",
  showFor: (a) => `SHOW DELL LAPTOPS FOR ${a}`,
  compareHead: "SIDE BY SIDE",
  storage: "Storage",
  display: "Display",
  price: "Price",
  stock: "Stock",
};

const LABELS: Record<string, CardLabels> = {
  en: EN_LABELS,
  hi: {
    specBrief: "स्पेक ब्रीफ",
    sizedFor: "इसके लिए",
    sizedForN: (n) => `${n} गेम्स के लिए`,
    config: "सुझाया गया कॉन्फ़िगरेशन",
    basis: "आधार",
    baseline: "सुझाया गया बेसलाइन",
    matchingLaptops: "मैचिंग डेल लैपटॉप",
    matchingDesktops: "मैचिंग डेल डेस्कटॉप",
    matchingBoth: "मैचिंग डेल लैपटॉप और डेस्कटॉप",
    pick: "हमारी पसंद",
    fits: "सही है",
    close: "करीब",
    overBudget: (a) => `बजट से ${a} ज़्यादा`,
    whichToBuy: "कौन सा लैपटॉप लेना चाहिए?",
    yourBudget: "आपका बजट",
    showFor: (a) => `${a} के लिए डेल लैपटॉप दिखाओ`,
    compareHead: "आमने-सामने",
    storage: "स्टोरेज",
    display: "डिस्प्ले",
    price: "कीमत",
    stock: "स्टॉक",
  },
  // Hinglish — Hindi written in Latin letters, which is how a large share
  // of this store's customers type. Without this set the card fell back to
  // English beside a Hinglish reply, which is the split the QA report
  // flagged (L-LANG).
  hinglish: {
    specBrief: "SPEC BRIEF",
    sizedFor: "ISKE LIYE",
    sizedForN: (n) => `${n} GAMES KE LIYE`,
    config: "SUGGESTED CONFIGURATION",
    basis: "AADHAR",
    baseline: "Suggested baseline",
    matchingLaptops: "MATCHING DELL LAPTOPS",
    matchingDesktops: "MATCHING DELL DESKTOPS",
    matchingBoth: "MATCHING DELL LAPTOPS AUR DESKTOPS",
    pick: "HAMARI PASAND",
    fits: "SAHI HAI",
    close: "KAREEB",
    overBudget: (a) => `budget se ${a} zyada`,
    whichToBuy: "KAUNSA LAPTOP LENA CHAHIYE?",
    yourBudget: "AAPKA BUDGET",
    showFor: (a) => `${a} KE LIYE DELL LAPTOPS DIKHAO`,
    compareHead: "AAMNE-SAAMNE",
    storage: "Storage",
    display: "Display",
    price: "Keemat",
    stock: "Stock",
  },
};

function labelsFor(language?: string): CardLabels {
  return LABELS[(language || "en").toLowerCase()] || EN_LABELS;
}

const BAR_WIDTHS = [94, 82, 70, 88, 100];

// "Far Cry 6 system requirements PC" -> "Far Cry 6". The searches are the only
// place EVERY game of a multi-game turn appears: the contract's own target
// field holds a single name, so a build sized for three games could only ever
// name one of them. Reading the titles back out of the queries is what lets
// the card show all of them.
const QUERY_NOISE =
  /\s*(?:pc|minimum|recommended|official)?\s*(?:system\s+)?(?:requirements?|specs?|spec)\s*(?:pc|for\s+pc)?\s*$/i;

// Words that say nothing about WHICH game this is, so two titles sharing only
// these are not the same game.
const TITLE_FILLER = new Set([
  "the", "of", "and", "a", "an", "edition", "special", "remastered",
  "deluxe", "ultimate", "definitive", "complete", "game", "pc",
]);

function titleWords(name: string): string[] {
  return name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 2 && !TITLE_FILLER.has(w));
}

// Words that mean this string is a hardware lookup rather than the name of
// something you play or run. Any one of them disqualifies it.
const SHOPPING_WORDS =
  /\b(?:laptop|laptops|notebook|desktop|pc|machine|system|requirements?|specs?|spec|gpu|cpu|ram|vram|ssd|storage|graphics|processor|memory|budget|price|best|top|recommended|20\d\d)\b/i;

// Only a REQUIREMENTS lookup names a game the build was sized against. The
// flow also searches to find out what exists ("FIFA game series titles") or
// what a genre contains ("most demanding racing games"), and those were being
// read as titles: one customer asking about a single game was shown a card
// headed "SIZED FOR 2 GAMES — FIFA game series titles, Football Club 27 game".
const REQUIREMENTS_QUERY = /\b(?:requirements?|specs?|spec)\b/i;

export function gamesResearched(queries: string[], target?: string | null): string[] {
  const out: string[] = [];
  for (const q of queries) {
    if (!REQUIREMENTS_QUERY.test(q)) continue;
    const name = q.replace(QUERY_NOISE, "").trim();
    // A genre lookup ("most demanding racing games") is not a title; it has no
    // business in a list headed with the games the build was sized for.
    if (!name || /\bgames\b/i.test(name)) continue;
    // Nor is a HARDWARE lookup. Reported live on a card for AI work:
    //   ✓ AI model training laptop system requirements GPU RAM CPU 2024
    // — the bot's own search string, printed as the thing the build was sized
    // for, and repeated in the sentence underneath it. QUERY_NOISE only strips
    // a trailing "system requirements"; a query that buries those words in the
    // middle, or mentions the parts by name, came through whole.
    if (SHOPPING_WORDS.test(name)) continue;
    // A later search for the SAME game replaces the earlier one, because that
    // is exactly what answering a clarification looks like across turns: "Far
    // Cry" is searched, the customer picks, "Far Cry 5" is searched next.
    //
    // Matched on a shared distinctive word rather than on one name extending
    // the other, because the resolved name often is not an extension: "The
    // Elder Scrolls Skyrim" and "Skyrim Special Edition" share no prefix in
    // either direction, so a prefix rule listed the one game twice — once
    // without the version the customer had just given.
    const words = titleWords(name);
    const clash = out.findIndex((existing) =>
      titleWords(existing).some((w) => words.includes(w)),
    );
    // The later name wins: it is the answer, the earlier one was the question.
    if (clash === -1) out.push(name);
    else out[clash] = name;
  }
  if (out.length === 0 && target) out.push(target);
  return out;
}

export function RecommendationCard({ contract, onAction, games: allGames }: Props) {
  const { use_case: uc, research, recommended_spec_profile: spec } = contract;
  const t = labelsFor(contract.language);
  const f = research.key_findings;
  const picks = contract.product_match ?? [];
  const singlePick = Boolean(contract.single_pick) && picks.length === 1;
  // Which product's offers are open, by card key. One at a time: the panel is
  // long, and two open at once would bury the other products entirely.
  const [openOffers, setOpenOffers] = useState<string | number | null>(null);

  const specRows = (
    [
      ["GPU", spec.gpu_tier],
      ["CPU", spec.cpu_tier],
      ["RAM", spec.ram_gb != null ? `${spec.ram_gb} GB` : null],
      ["STORAGE", spec.storage],
      ["DISPLAY", spec.display],
    ] as [string, string | null][]
  ).filter((r): r is [string, string] => Boolean(r[1]));


  const categoryLabel = uc.category
    ? (CATEGORY_LABEL[uc.category] ?? uc.category)
    : null;
  // What the server says this build covers, when it says so. Preferred over
  // anything read off the search strings: a KIND of game has no title in its
  // query — it searches "most demanding racing games" — so gamesResearched
  // could never show it, however well the rest of the turn handled it.
  //
  // Reported live: "gta, Far Cry 2, God of War and car racing game" was
  // answered about all four and shown a card headed SIZED FOR 3 GAMES, with
  // racing nowhere on it.
  const games = contract.covers?.length
    ? contract.covers
    : allGames?.length
      ? allGames
      : gamesResearched(research.queries_run, uc.target);

  // One short line saying what this configuration is for, so the specs below
  // arrive with a reason attached rather than as a wall of numbers.
  const gameList =
    games.length > 2
      ? `${games.slice(0, -1).join(", ")} and ${games[games.length - 1]}`
      : games.join(" and ");
  const configNote = gameList
    ? `The best setup for ${gameList} — based on what each one actually needs.`
    : "A well-rounded setup that handles a wide range of modern games.";
  // The heading names the game only when there IS one game. A build covering
  // three of them was headed "Call of Duty: Warzone 2" — whichever single
  // title the contract happened to carry — which reads as a card about that
  // game and quietly drops the other two the customer asked about. Reported
  // live, beside a SIZED FOR 3 GAMES row listing all three.
  //
  // With more than one, the heading steps back to the build itself; the row
  // below names every game, ticked, which is where a list belongs.
  const namesOneGame = games.length <= 1 && uc.target;
  const title = namesOneGame
    ? `${uc.target}${uc.detail ? ` · ${uc.detail}` : ""}`
    : categoryLabel
      ? `${categoryLabel} build`
      : "Your build";

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.tag}>{t.specBrief}</span>
        <span className={styles.title}>{title}</span>
      </div>

      {/* Every game this build was sized for, not just the one the contract
          happens to carry. "USE CASE: Gaming" used to sit here and said
          nothing — this desk covers only gaming, so the tile was a label with
          no information in it, on a card where space is the scarce thing. */}
      {games.length > 0 && (
        <div className={styles.sized}>
          <div className={styles.sizedHead}>
            <span className={styles.tk}>
              {games.length > 1 ? t.sizedForN(games.length) : t.sizedFor}
            </span>
            <span className={styles.sizedBasis}>
              {research.source_quality
                ? (SOURCE_LABEL[research.source_quality] ?? research.source_quality)
                : t.baseline}
            </span>
          </div>
          <div className={styles.gameList}>
            {games.map((g: string) => (
              <span key={g} className={styles.game}>
                <span className={styles.gameTick} aria-hidden="true">
                  ✓
                </span>
                {g}
              </span>
            ))}
          </div>
        </div>
      )}
      {games.length === 0 && (
        <div className={styles.tiles}>
          <div className={styles.tile}>
            <div className={styles.tk}>{t.basis}</div>
            <div className={styles.tv}>
              {research.source_quality
                ? (SOURCE_LABEL[research.source_quality] ?? research.source_quality)
                : t.baseline}
            </div>
          </div>
        </div>
      )}

      {/* One configuration, stated once. The same figures used to appear three
          times over: a big "8 GB recommended VRAM", then "RTX 4060-class, 8GB
          VRAM" in the GPU row, then "REC. VRAM 8 GB" in a chip below — and RAM
          twice the same way. Three readings of one number is not three facts,
          it just made the card hard to read. */}
      {specRows.length > 0 && (
        <div className={styles.config}>
          <div className={styles.configHead}>
            <span className={styles.tag}>{t.config}</span>
          </div>
          <p className={styles.configNote}>{configNote}</p>
          <div className={styles.bars}>
            {specRows.map(([label, value], i) => (
              <div key={label} className={styles.barRow}>
                <span className={styles.barLabel}>{label}</span>
                <span className={styles.barTrack}>
                  <span
                    className={styles.barFill}
                    style={{ width: `${BAR_WIDTHS[i % BAR_WIDTHS.length]}%` }}
                  />
                </span>
                <span className={styles.barVal} title={value}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}


      {f.notes && <div className={styles.notes}>{f.notes}</div>}

      {/* Asked to see them side by side. The same picks, as rows against
          spec columns — built from the catalog rows the card already has, so
          the model never writes a figure and cannot get one wrong. Reported:
          "Compare all three laptops in a table" produced no table at all. */}
      {picks.length > 1 && contract.compare && (
        <div className={styles.compare}>
          <div className={styles.tag}>{t.compareHead}</div>
          <div className={styles.compareScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th />
                  {picks.map((p) => (
                    <th key={p.order_code}>{p.product_name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ["GPU", (p: ProductMatch) => p.graphics_card],
                    ["CPU", (p: ProductMatch) => p.processor],
                    ["RAM", (p: ProductMatch) => p.memory],
                    [t.storage, (p: ProductMatch) => p.storage],
                    [t.display, (p: ProductMatch) => p.display],
                    [t.price, (p: ProductMatch) => p.price],
                    [t.stock, (p: ProductMatch) => p.availability],
                  ] as const
                ).map(([label, read]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    {picks.map((p) => (
                      <td key={p.order_code}>{read(p) || "—"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {picks.length > 0 && (
        <div className={styles.picks}>
          <div className={styles.picksHead}>
            {singlePick
              ? picks[0].form_factor === "desktop"
                ? `${t.pick} · DESKTOP`
                : t.pick
              : picks.every((p) => p.form_factor === "desktop")
                ? t.matchingDesktops
                : picks.some((p) => p.form_factor === "desktop")
                  ? t.matchingBoth
                  : t.matchingLaptops}
          </div>
          {picks.map((p, i) => {
            const chips = [
              p.gpu_model && p.vram_gb ? `${p.gpu_model} ${p.vram_gb}GB` : p.gpu_model,
              p.ram_gb ? `${p.ram_gb}GB RAM` : null,
            ].filter(Boolean) as string[];
            // Revealed on hover — the full sheet doesn't fit the compact row.
            const detail = (
              [
                ["CPU", p.processor],
                ["GPU", p.graphics_card],
                ["RAM", p.memory],
                ["DISK", p.storage],
                ["SCREEN", p.display],
                ["WIRELESS", p.wireless ?? null],
              ] as [string, string | null][]
            ).filter(
              (r): r is [string, string] => Boolean(r[1]) && r[1]!.trim() !== "-",
            );
            // Store promotions, rendered only when this product actually has
            // some — see OffersBar. Kept whole rather than trimmed to a few:
            // the panel is opened deliberately, so it can afford the detail.
            const offers = (p.offers ?? []).filter(
              (o) => o && o.heading && o.heading.trim() && o.heading.trim() !== "-",
            );
            const key = p.order_code || i;
            const offersOpen = openOffers === key;
            // The whole card is the link, rather than a "View" button inside
            // it: the entire row is the thing being chosen, so anything less
            // than all of it is a smaller target than the customer expects.
            // Still a real <a href>, so focus, middle-click, cmd-click and
            // "copy link address" all behave normally.
            const body = (
              <>
                <div className={styles.pickTop}>
                  <span className={styles.pickName}>
                    {p.product_name}
                    {p.form_factor === "desktop" && (
                      <span className={styles.pickKind}> · Desktop</span>
                    )}
                  </span>
                  <span
                    className={p.meets_spec ? styles.pickFit : styles.pickClose}
                  >
                    {p.meets_spec ? t.fits : t.close}
                  </span>
                </div>
                <div className={styles.pickSpecs}>
                  {chips.length > 0 ? chips.join("  ·  ") : p.graphics_card}
                </div>
                <div className={styles.pickMeta}>
                  <span className={styles.pickPrice}>{p.price || "—"}</span>
                  {/* Over the budget they set, said on the face of the card.
                      Reported: "under 1.3 lakh strictly" was answered with a
                      ₹1,42,490 machine badged FITS and nothing visible to say
                      it was over — the wording existed, in an expandable note
                      nobody opens. */}
                  {typeof p.over_budget_inr === "number" &&
                    p.over_budget_inr > 0 && (
                      <span className={styles.pickOver}>
                        {t.overBudget(`₹${p.over_budget_inr.toLocaleString("en-IN")}`)}
                      </span>
                    )}
                  {p.availability && (
                    <span className={styles.pickStock}>{p.availability}</span>
                  )}
                </div>
                {(detail.length > 0 || p.match_note) && (
                  <div className={styles.pickDetail}>
                    {detail.map(([k, v]) => (
                      <div key={k} className={styles.detailRow}>
                        <span className={styles.detailKey}>{k}</span>
                        <span className={styles.detailVal}>{v}</span>
                      </div>
                    ))}
                    {p.match_note && (
                      <div className={styles.detailNote}>{p.match_note}</div>
                    )}
                  </div>
                )}
                {/* Last thing in the card, and always visible — the hover
                    sheet above opens and closes independently of it. */}
                {offers.length > 0 && (
                  <OffersBar
                    offers={offers}
                    open={offersOpen}
                    onToggle={() => setOpenOffers(offersOpen ? null : key)}
                  />
                )}
              </>
            );
            // The store link is a stretched OVERLAY rather than a wrapper.
            // Wrapping everything in one <a> keeps the whole card clickable
            // (which is the point) but makes the offers button unusable —
            // a <button> inside an <a> is invalid HTML, and every click just
            // navigates to Dell. As an overlay the card still clicks through
            // to the product anywhere, while the offers button and its T&C
            // links sit above it and get their own clicks.
            // No store link -> no overlay, so the card never looks clickable
            // when it would go nowhere.
            return (
              <div key={key} className={styles.pick}>
                {p.product_url && (
                  <a
                    className={styles.pickLink}
                    href={p.product_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={p.product_name}
                  />
                )}
                {body}
              </div>
            );
          })}
        </div>
      )}

      {/* The raw query strings are deliberately NOT shown. "The Elder Scrolls
          Skyrim Special Edition system requirements PC" is the plumbing of the
          answer, not part of it; the games themselves lead the card instead,
          and the BASIS line already says whether real sources backed them. */}

      {picks.length > 0 && (
        <div className={styles.ctas}>
          {!singlePick && (
            <button
              type="button"
              className={styles.ctaPrimary}
              onClick={() => onAction("So which laptop should I buy?")}
            >
              {t.whichToBuy}
            </button>
          )}
          {/* "Tier down?" and "Talk to support" are hidden for now at the
              user's request. Both still work as typed messages — the flow
              behind them is untouched, only the buttons are gone. */}
        </div>
      )}
    </div>
  );
}
